import React, { useEffect, useRef, useContext, useState } from 'react';
import { PaymentMethod, Stripe } from '@stripe/stripe-js';
import { Product } from '../../../deprecated/payment/types';
import ContentCardLabel from '../../../../components/content/content-card-label';
import {
  getAddOnChallengeProducts,
  getNutritionCoachProductPrice,
} from './utils/challengeAddon.util';
import { ChallengeAddOns } from './container/ChallengeAddons';
import { useDecision } from '@optimizely/react-sdk';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { setChallengeModalOpen } from 'src/store/payment';
import { AddonPurchaseConfirmation } from './modal/PurchaseConfirmation';
import { PersonalCoachPaymentModal } from './modal/PersonalCoachPaymentModal';
import { PersonalCoachRemoveConfirmationModal } from './modal/PersonalCoachRemoveConfirmationModal';
import { PersonalCoachSuccessModal } from './modal/PersonalCoachSuccessModal';
import { ReactivateAndPurchaseModal } from './modal/ReactivateAndPurchaseModal';
import paymentAPI from '../../../deprecated/payment/api';
import { ToastContext } from 'src/components/toast/toast';
import { CircularProgress, Typography, Stack } from '@mui/material';
import Popup from 'src/components/content/popup/popup';
import ContentWrapper from 'src/components/content/content-wrapper';
import ContentSection from 'src/components/content/content-section';
import colors from 'src/styles/equalution/colors';
import styles from './modal/modal.module.scss';
import { checkAuthentication } from 'src/lib/auth/actions';
import { useDispatch } from 'react-redux';
import { isUserFromAustralia } from 'src/lib/utils';
import { CoachingCardAddons } from './components/CoachingCardAddons';
import { useNutritionCoachAddon } from './hooks/useNutritionCoachAddon';
import ProductService from 'src/services/product-service';
import {
  trackAddonViewed,
  trackAddonSelected,
  trackAddonPaymentCompleted,
  trackAddonPurchaseCompleted,
  trackAddonPaymentFailed,
} from 'src/util/track';

interface OwnProps {
  paymentMethod: PaymentMethod | undefined;
  products: Product[] | undefined;
  setRegetProducts: (reget: boolean) => void;
  stripePromise?: Promise<Stripe | null>;
  setPaymentMethod?: (paymentMethod: PaymentMethod) => void;
}

const ExtrasStep = ({
  paymentMethod,
  products = [],
  setRegetProducts,
  stripePromise,
  setPaymentMethod,
}: OwnProps): JSX.Element => {
  const reduxDispatch = useDispatch();

  const [localPaymentMethod, setLocalPaymentMethod] = useState<
    PaymentMethod | undefined
  >(paymentMethod);

  useEffect(() => {
    setLocalPaymentMethod(paymentMethod);
  }, [paymentMethod]);

  const handlePaymentMethodUpdate = (newPaymentMethod: PaymentMethod) => {
    setLocalPaymentMethod(newPaymentMethod);
    if (setPaymentMethod) {
      setPaymentMethod(newPaymentMethod);
    }
  };
  const dispatch = useAppDispatch();
  const { challengeModalOpen } = useAppSelector(state => state.payment);
  const { user } = useAppSelector(state => state.auth);

  const challengeProducts = getAddOnChallengeProducts(products);
  const showChallengeProducts = challengeProducts.length > 0;

  const afterpaySuccessProcessedRef = useRef<boolean>(false);
  const { addToast } = useContext(ToastContext);
  const [isCompletingAfterpay, setIsCompletingAfterpay] = useState(false);

  const [decision] = useDecision('enable_challenge_addons', {
    autoUpdate: true,
  });

  const hasChallengeAddons = showChallengeProducts && decision.enabled;

  // Personal Coach Addon hook
  const {
    coachingEndDate,
    nextBillingDate,
    showNutritionCoachAddon,
    hasSubscribedToNutritionCoachAddon,
    isProcessingNutritionCoach,
    showPaymentModal,
    showRemoveConfirmationModal,
    showSuccessModal,
    handleAddNutritionCoach,
    handleRemoveNutritionCoach,
    handleClosePaymentModal,
    handleCloseRemoveConfirmationModal,
    handleCloseSuccessModal,
    handleConfirmRemove,
    handleConfirmPurchase,
    setSelectedPaymentType,
    triggerSuccessModal,
    closePaymentModal,
    nutritionCoachProduct,
    showReactivateModal,
    setShowReactivateModal,
    isReactivating,
    reactivateError,
    handleConfirmReactivateAndPurchase,
  } = useNutritionCoachAddon({
    products,
    user,
    paymentMethod: localPaymentMethod,
    setRegetProducts,
  });

  const hasTrackedViewRef = useRef(false);
  useEffect(() => {
    if (products && products.length > 0 && !hasTrackedViewRef.current) {
      const available: string[] = [];
      if (showNutritionCoachAddon) {
        available.push('nutrition_coaching');
      }
      if (hasChallengeAddons) {
        available.push('challenge_addons');
      }
      if (available.length > 0) {
        trackAddonViewed(available.join(','));
        hasTrackedViewRef.current = true;
      }
    }
  }, [products, showNutritionCoachAddon, hasChallengeAddons]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const afterpaySuccess = urlParams.get('afterpay_okay');
    const paymentIntentId = urlParams.get('payment_intent');
    const flow = urlParams.get('flow');

    if (afterpaySuccess === 'true' && !afterpaySuccessProcessedRef.current) {
      afterpaySuccessProcessedRef.current = true;
      if (paymentIntentId) {
        setIsCompletingAfterpay(true);
        completeAfterPayPurchase(paymentIntentId, flow === 'nutrition_coach');
      } else {
        // Handle case without payment intent (fallback)
        if (flow === 'nutrition_coach') {
          // For Nutrition Coach, close payment modal and show success modal
          closePaymentModal();
          triggerSuccessModal();
        } else {
          dispatch(setChallengeModalOpen({ isConfirmationModalOpen: true }));
        }
      }
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const completeAfterPayPurchase = async (
    paymentIntentId: string,
    isNutritionCoachFlow: boolean = false
  ) => {
    const urlParams = new URLSearchParams(window.location.search);
    const addonType =
      urlParams.get('addon_type') ||
      (isNutritionCoachFlow ? 'nutrition_coaching' : '');
    const totalPriceVal = Number(urlParams.get('total_price') || '0');
    const priceIdsVal = urlParams.get('price_ids') || '';
    const hasCoach = urlParams.get('has_coach') === 'true';
    const challengePrice = Number(urlParams.get('challenge_price') || '0');
    const coachPrice = Number(urlParams.get('coach_price') || '0');

    try {
      if (!user?.id) {
        addToast('User not found', 'danger', 5000);
        setIsCompletingAfterpay(false);
        return;
      }
      let response: any;
      if (isNutritionCoachFlow) {
        response = await ProductService.purchaseNutritionCoachingAddon({
          userId: user.id,
          email: user.email,
          paymentMethodId: paymentMethod?.id || '',
          priceIds: [
            getNutritionCoachProductPrice(products || [])?.priceId || '',
          ],
          isAfterPay: true,
          isCardPayment: false,
          paymentIntentId,
        });
      } else {
        response = await paymentAPI.post('/afterpay/complete-addon-purchase', {
          paymentIntentId,
          userId: user.id,
        });
      }
      if (response.data.data === true) {
        reduxDispatch(checkAuthentication());
        setRegetProducts(true);
        if (isNutritionCoachFlow) {
          const ncPrice =
            getNutritionCoachProductPrice(products || [])?.price || 0;
          const finalPrice = totalPriceVal || ncPrice;
          trackAddonSelected('nutrition_coaching', 'afterpay', finalPrice);
          trackAddonPaymentCompleted(
            'afterpay',
            'nutrition_coaching',
            finalPrice
          );
          trackAddonPurchaseCompleted(
            'afterpay',
            'nutrition_coaching',
            finalPrice,
            priceIdsVal || undefined
          );

          // For Nutrition Coach, close payment modal and show success modal
          closePaymentModal();
          triggerSuccessModal();
        } else {
          if (addonType) {
            trackAddonSelected(
              addonType,
              'afterpay',
              challengePrice || totalPriceVal
            );
          }
          if (hasCoach) {
            trackAddonSelected(
              'personal_nutrition_coach',
              'afterpay',
              coachPrice
            );
          }
          const primaryAddon =
            addonType ||
            (hasCoach ? 'personal_nutrition_coach' : 'challenge_addon');
          trackAddonPaymentCompleted('afterpay', primaryAddon, totalPriceVal);
          trackAddonPurchaseCompleted(
            'afterpay',
            primaryAddon,
            totalPriceVal,
            priceIdsVal
          );

          // For Challenge Addons, show confirmation modal
          dispatch(setChallengeModalOpen({ isConfirmationModalOpen: true }));
        }
      }
    } catch (error) {
      const errMessage =
        (error as any)?.message || 'Failed to complete Afterpay purchase';
      const failAddonType =
        addonType ||
        (isNutritionCoachFlow ? 'nutrition_coaching' : 'challenge_addon');
      trackAddonPaymentFailed(
        'afterpay',
        failAddonType,
        'afterpay_completion',
        errMessage,
        totalPriceVal
      );
      addToast('Failed to complete purchase', 'danger', 5000);
      console.error('Error completing afterpay purchase:', error);
    } finally {
      setIsCompletingAfterpay(false);
    }
  };

  const handleCloseModal = () => {
    dispatch(setChallengeModalOpen({ isConfirmationModalOpen: false }));
  };

  // Determine what to show based on available addons

  // Render modals (shared across all scenarios)
  const renderModals = () => (
    <>
      {Boolean(challengeModalOpen?.isConfirmationModalOpen) && (
        <AddonPurchaseConfirmation onClose={handleCloseModal} />
      )}
      {isCompletingAfterpay && (
        <Popup onClose={() => {}}>
          <ContentWrapper className={styles.background}>
            <ContentSection className={styles.confirmContent} collapseAt="sm">
              <Stack alignItems={'center'} mt={3}>
                <CircularProgress
                  size={60}
                  sx={{ color: colors.SALAD, mb: 2 }}
                />

                <Typography
                  mt={2}
                  fontSize={32}
                  fontWeight={700}
                  color={colors.OFF_BLACK}
                  lineHeight={'42px'}
                  textAlign="center"
                  paddingX={'32px'}
                  gutterBottom>
                  Completing your purchase...
                </Typography>

                <Typography
                  mt={3}
                  fontSize={20}
                  fontWeight={600}
                  color={colors.BLUEBERRY}
                  textAlign="center"
                  gutterBottom>
                  Please wait while we process your Afterpay payment
                </Typography>
              </Stack>
            </ContentSection>
          </ContentWrapper>
        </Popup>
      )}

      {/* Personal Coach Modals */}
      {showPaymentModal && nutritionCoachProduct && (
        <PersonalCoachPaymentModal
          onClose={handleClosePaymentModal}
          onConfirm={handleConfirmPurchase}
          isLoading={isProcessingNutritionCoach}
          paymentMethod={localPaymentMethod}
          stripePromise={stripePromise || Promise.resolve(null)}
          setPaymentMethod={handlePaymentMethodUpdate}
          price={getNutritionCoachProductPrice(products || [])?.price || 0}
          onPaymentTypeChange={setSelectedPaymentType}
        />
      )}

      {showRemoveConfirmationModal && (
        <PersonalCoachRemoveConfirmationModal
          onClose={handleCloseRemoveConfirmationModal}
          onConfirm={handleConfirmRemove}
          isLoading={isProcessingNutritionCoach}
        />
      )}

      {showSuccessModal && (
        <PersonalCoachSuccessModal onClose={handleCloseSuccessModal} />
      )}

      {showReactivateModal && (
        <ReactivateAndPurchaseModal
          isOpen={showReactivateModal}
          onClose={() => setShowReactivateModal(false)}
          isReactivating={isReactivating}
          reactivateError={reactivateError}
          onConfirm={handleConfirmReactivateAndPurchase}
        />
      )}
    </>
  );

  // Scenario 1: Both Nutrition Coach and Challenge Addons
  if (showNutritionCoachAddon && hasChallengeAddons) {
    return (
      <>
        <div style={{ marginBottom: '24px' }}>
          <CoachingCardAddons
            products={products}
            isSubscribed={hasSubscribedToNutritionCoachAddon}
            onAdd={handleAddNutritionCoach}
            onRemove={handleRemoveNutritionCoach}
            isLoading={isProcessingNutritionCoach}
            endDate={coachingEndDate}
            nextBillingDate={nextBillingDate}
          />
        </div>
        <ChallengeAddOns
          hideChallengePersonalCoachAddon={
            !isUserFromAustralia() || hasSubscribedToNutritionCoachAddon
          }
          products={products}
          paymentMethod={localPaymentMethod}
          refetchProduct={() => {
            reduxDispatch(checkAuthentication());
            setRegetProducts(true);
          }}
          stripePromise={stripePromise || Promise.resolve(null)}
          setPaymentMethod={handlePaymentMethodUpdate}
        />
        {renderModals()}
      </>
    );
  }

  // Scenario 2: Only Challenge Addons
  if (hasChallengeAddons) {
    return (
      <>
        <ChallengeAddOns
          hideChallengePersonalCoachAddon={
            !isUserFromAustralia() || hasSubscribedToNutritionCoachAddon
          }
          products={products}
          paymentMethod={localPaymentMethod}
          refetchProduct={() => {
            reduxDispatch(checkAuthentication());
            setRegetProducts(true);
          }}
          stripePromise={stripePromise || Promise.resolve(null)}
          setPaymentMethod={handlePaymentMethodUpdate}
        />
        {renderModals()}
      </>
    );
  }

  // Scenario 3: Only Nutrition Coach Addon
  if (showNutritionCoachAddon) {
    return (
      <>
        <div style={{ marginBottom: '24px' }}>
          <CoachingCardAddons
            products={products}
            isSubscribed={hasSubscribedToNutritionCoachAddon}
            onAdd={handleAddNutritionCoach}
            onRemove={handleRemoveNutritionCoach}
            isLoading={isProcessingNutritionCoach}
            endDate={coachingEndDate}
            nextBillingDate={nextBillingDate}
          />
        </div>
        {renderModals()}
      </>
    );
  }

  // Scenario 4: No add-ons available
  return (
    <>
      <ContentCardLabel>
        <div>No add-ons available</div>
      </ContentCardLabel>
      {renderModals()}
    </>
  );

  // This code is not meant to be displayed as those Addons are no longer supported
  // return (
  //   <>
  //     {products &&
  //       Object.entries(products)
  //         .sort()
  //         .map(([id, product]: [string, Product]) => {
  //           const isConsumable = Object.prototype.hasOwnProperty.call(
  //             product.metadata,
  //             'consumable'
  //           );
  //           if (isConsumable) {
  //             pushToArray(consumableProducts, product);
  //           }
  //           if (
  //             product.name === 'Social Amendment' &&
  //             (user?.plan?.plan_id || 0) === PLAN_ID.STANDARD
  //           ) {
  //             return <Fragment key={id} />;
  //           }
  //           return Object.values(product.prices).map((price): JSX.Element => {
  //             return isConsumable && price.active ? (
  //               <Fragment key={price.id}>
  //                 <ContentCardLabel>
  //                   <div>
  //                     {product.name.toUpperCase() ===
  //                     'Additional Meal Plan Option'.toUpperCase()
  //                       ? additionalMealPlanOptionNominal
  //                       : product.name}
  //                   </div>
  //                 </ContentCardLabel>
  //                 <ContentCard lighter className={styles.card}>
  //                   <ExtrasPreview
  //                     label={product.name}
  //                     qty={product?.quantity || 0}
  //                     setPopupVisible={setPopupVisible}
  //                   />
  //                   <Button
  //                     size="sm"
  //                     outline
  //                     onClick={(): void => {
  //                       setPopupVisible(true);
  //                       setSelectedProduct(product);
  //                       setSelectedPriceID(price.id);
  //                     }}>
  //                     Add
  //                   </Button>
  //                 </ContentCard>
  //               </Fragment>
  //             ) : (
  //               <Fragment key={price.id} />
  //             );
  //           });
  //         })}
  //     {popupVisible && selectedProduct && (
  //       <ExtrasPopup
  //         setSuccessMessage={setSuccessMessage}
  //         selectedProduct={selectedProduct}
  //         consumableProducts={consumableProducts}
  //         paymentMethod={paymentMethod}
  //         totalPrice={totalPrice}
  //         selectedPriceID={selectedPriceID}
  //         selectedPriceIDs={selectedPriceIDs}
  //         setSelectedPriceIDs={setSelectedPriceIDs}
  //         setPopupVisible={setPopupVisible}
  //         setTotalPrice={setTotalPrice}
  //         setSelectedProduct={setSelectedProduct}
  //       />
  //     )}
  //   </>
  // );
};

export default ExtrasStep;
